import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../components/tables/basic/U2000BasicTableList";
import { getCriteriaApiFieldName } from "../components/tables/commons/search/U2000CriteriaLookupRegistry";
import type { QuickSearchData } from "../context/logicalUnit/U2000LogicalUnitContext";
import type { U2000SearchParameter } from "../reducer/formReducer/U2000SearchState";
import { resolveEffectiveIncludeConditionsForColumn } from "./u2000SearchCriteriaFieldConditions";

/** Session capturée au moment de la consommation Quick Search (pour onSuccess après clear context). */
export interface U2000QuickSearchSession {
  isFromQuickSearch: boolean;
  targetLogicalUnit: string;
}

/** Préfixe d’UL (`/U3121`) pour savoir si on a quitté l’écran QS. */
export function u2000LogicalUnitPathPrefix(pathname: string): string {
  const match = pathname.match(/^\/U\d+/i);
  return match ? match[0].toUpperCase() : '';
}

/** Filtre sauvegardé par défaut : ne pas l’appliquer pendant une session Quick Search. */
export function u2000ShouldSkipSavedDefaultFilter(quickSearchActive: boolean, skipDefaultFilter: boolean): boolean {
  return quickSearchActive || skipDefaultFilter;
}

/**
 * Ne relâcher le skip qu’après être arrivé sur l’UL QS, puis en être parti.
 * Sinon un QS Projet → Employé voit encore `/U3121` et annule le skip trop tôt.
 */
export function u2000QuickSearchSkipDefaultFilterTransition(params: {
  targetPrefix: string | null;
  currentPrefix: string;
  hasLandedOnTarget: boolean;
}): { hasLandedOnTarget: boolean; releaseSkip: boolean } {
  const { targetPrefix, currentPrefix, hasLandedOnTarget } = params;
  if (!targetPrefix) {
    return { hasLandedOnTarget: false, releaseSkip: false };
  }
  if (currentPrefix === targetPrefix) {
    return { hasLandedOnTarget: true, releaseSkip: false };
  }
  if (hasLandedOnTarget && currentPrefix !== '' && currentPrefix !== targetPrefix) {
    return { hasLandedOnTarget: false, releaseSkip: true };
  }
  return { hasLandedOnTarget, releaseSkip: false };
}

/** true si les critères liste contiennent déjà le champ/valeur Quick Search. */
export function u2000CriteriasIncludeQuickSearchValue(
  criterias: Array<{ field?: { name?: string }; value?: unknown }>,
  fieldName: string,
  value: string,
): boolean {
  return criterias.some((c) => c.field?.name === fieldName && String(c.value ?? '') === String(value));
}

/** Payload POST search : un seul critère QS (jamais AND avec le filtre défaut). */
export function u2000BuildQuickSearchSearchParameter(
  base: U2000SearchParameter,
  field: U2000TableColumn,
  operator: string,
  value: string | number | boolean,
): U2000SearchParameter {
  const defaultFields =
    base.fields.length > 0
      ? base.fields
      : [{ name: field.name }];
  return {
    fields: defaultFields,
    sortedFields: base.sortedFields,
    criterias: [
      {
        field: getCriteriaApiFieldName(field),
        operator,
        value,
        type: field.type,
      },
    ],
  };
}

/**
 * Jeton stable d'une action Quick Search (anti-rejeu d'effet).
 */
export function u2000BuildQuickSearchConsumeToken(data: Pick<QuickSearchData, "fieldName" | "condition" | "value"> & { targetLogicalUnit?: string }): string {
  return `${data.targetLogicalUnit ?? ''}|${data.fieldName}|${data.condition}|${data.value}`;
}

/**
 * true si le jeton n'a pas encore été consommé (même action).
 * false si déjà en cours / déjà traité pour ce jeton.
 */
export function u2000ShouldConsumeQuickSearchToken(token: string, processingToken: string | null): boolean {
  if (!token) {
    return false;
  }
  return processingToken !== token;
}

/**
 * Indique si la route courante appartient au périmètre d'une unité logique
 * (page liste ou formulaire de la même UL).
 */
export function u2000ShouldQuickSearchOpenForm(pathname: string, targetLogicalUnit: string): boolean {
  if (!targetLogicalUnit) {
    return false;
  }
  return pathname === targetLogicalUnit || pathname.startsWith(`${targetLogicalUnit}/`);
}

/**
 * Construit le chemin absolu vers le formulaire d'une entité depuis le quick search.
 */
export function u2000BuildQuickSearchFormPath(targetLogicalUnit: string, entityId: string | number): string {
  return `${targetLogicalUnit}/${entityId}`;
}

/**
 * Fonction utilitaire pour obtenir la condition selon le type de champ.
 * Centralise la logique de recherche de condition pour éviter la duplication.
 */
export function getConditionForField(
  fieldType: string,
  conditionName: string,
  fieldColumn: U2000TableColumn,
  t: TFunction
): { name: string; label: string; isRequiredValue: boolean } | null {
  const stringConditions = [
    { name: "equals", label: t("lblCndEquals"), isRequiredValue: true },
    { name: "notequals", label: t("lblCndNotEquals"), isRequiredValue: true },
    { name: "contains", label: t("lblCndContains"), isRequiredValue: true },
    { name: "startswith", label: t("lblCndStartsWith"), isRequiredValue: true },
    { name: "endswith", label: t("lblCndEndsWith"), isRequiredValue: true },
    { name: "in", label: t("lblCndIn"), isRequiredValue: true },
    { name: "isNull", label: t("lblCndIsNull"), isRequiredValue: false },
    { name: "isNotNull", label: t("lblCndIsNotNull"), isRequiredValue: false },
    { name: "interval", label: t("lblCndInterval"), isRequiredValue: true },
  ];

  const numberConditions = [
    { name: "equals", label: t("lblCndEquals"), isRequiredValue: true },
    { name: "notequals", label: t("lblCndNotEquals"), isRequiredValue: true },
    { name: "greaterthan", label: t("lblCndGreaterThan"), isRequiredValue: true },
    { name: "greaterthanorequal", label: t("lblCndGreaterThanOrEquals"), isRequiredValue: true },
    { name: "lesthan", label: t("lblCndLessThan"), isRequiredValue: true },
    { name: "lesthanorequal", label: t("lblCndLessThanOrEquals"), isRequiredValue: true },
    { name: "isNull", label: t("lblCndIsNull"), isRequiredValue: false },
    { name: "isNotNull", label: t("lblCndIsNotNull"), isRequiredValue: false },
  ];

  const dateConditions = [
    { name: "equals", label: t("lblCndEquals"), isRequiredValue: true },
    { name: "notequals", label: t("lblCndNotEquals"), isRequiredValue: true },
    { name: "greaterthan", label: t("lblCndGreaterThan"), isRequiredValue: true },
    { name: "greaterthanorequal", label: t("lblCndGreaterThanOrEquals"), isRequiredValue: true },
    { name: "lesthan", label: t("lblCndLessThan"), isRequiredValue: true },
    { name: "lesthanorequal", label: t("lblCndLessThanOrEquals"), isRequiredValue: true },
    { name: "isNull", label: t("lblCndIsNull"), isRequiredValue: false },
    { name: "isNotNull", label: t("lblCndIsNotNull"), isRequiredValue: false },
    { name: "interval", label: t("lblCndInterval"), isRequiredValue: true },
  ];

  let availableConditions: Array<{ name: string; label: string; isRequiredValue: boolean }> = [];
  if (fieldType === "string") {
    availableConditions = stringConditions;
  } else if (fieldType === "number") {
    availableConditions = numberConditions;
  } else if (fieldType === "date") {
    availableConditions = dateConditions;
  }

  // Filtrer selon include effectif (BL-0404 lookup → equals/in) / excludeConditions
  const effectiveInclude = resolveEffectiveIncludeConditionsForColumn(fieldColumn);
  let filteredConditions = availableConditions;
  if (effectiveInclude !== undefined) {
    filteredConditions = availableConditions.filter((c) => effectiveInclude.includes(c.name));
  } else if (fieldColumn.excludeConditions) {
    filteredConditions = availableConditions.filter((c) => !fieldColumn.excludeConditions!.includes(c.name));
  }

  const matched = filteredConditions.find((c) => c.name === conditionName);
  if (matched) {
    return matched;
  }

  // Repli Quick Search (BL-0408) : menu QS envoie encore contains alors que lookup n'autorise que equals/in
  const equalsFallback = filteredConditions.find((c) => c.name === 'equals' && c.isRequiredValue);
  if (equalsFallback) {
    return equalsFallback;
  }
  return filteredConditions.find((c) => c.isRequiredValue) ?? null;
}
